// pages/hr/wizard/steps/DocumentSigningStep.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemContent,
  ListItemDecorator,
  Chip,
  Checkbox,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalDialog,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/joy';
import {
  FileText as DescriptionOutlined,
  CheckCircle as CheckCircleOutlined,
  Clock as PendingOutlined,
  Pencil as DrawOutlined,
  DownloadSimple as DownloadOutlined,
  X as Close,
} from '@phosphor-icons/react';
import '../../../../lib/pdfWorkerSetup'; // Initialize PDF worker
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { SignatureModal } from '../../../../components/crm/contracts/SignatureModal';
import { useSignOnboardingDocument } from '../../../../hooks/useHr';
import type {
  WizardStepProps,
  PublicOnboardingSession,
  OnboardingRequiredDocument,
} from '../../../../types/hr';

interface ExtendedWizardStepProps extends WizardStepProps {
  sessionData?: PublicOnboardingSession & {
    required_documents?: OnboardingRequiredDocument[];
  };
  token?: string;
}

// How close to the bottom counts as having reached the end. Sub-pixel scroll
// positions and zoomed browsers never land exactly on zero.
const SCROLL_END_SLACK_PX = 24;

// No fallback document list. Inventing an "Employee Handbook Acknowledgment"
// when the package has no templates attached told the hire they had signed a
// document that does not exist, and told the employer they held a record of it.

const errorMessage = (err: unknown): string => {
  // DRF field errors ({"consent_agreed": ["..."]}) and the plain
  // {"error": "..."} envelope both arrive on `responseData`.
  const data = (err as { responseData?: Record<string, unknown> } | null)?.responseData;
  if (data) {
    if (typeof data.error === 'string') return data.error;
    const fieldError = Object.values(data).find(
      (value) => Array.isArray(value) && typeof value[0] === 'string'
    ) as string[] | undefined;
    if (fieldError) return fieldError[0];
  }
  return 'We could not record your signature. Please try again.';
};

interface SignDocumentModalProps {
  doc: OnboardingRequiredDocument;
  token: string;
  defaultSignerName: string;
  onClose: () => void;
  onSigned: (doc: OnboardingRequiredDocument) => void;
}

/**
 * Read the document, consent to signing electronically, type your name, then
 * draw the signature. The signature itself is captured by the same
 * SignatureModal the CRM uses for customer contracts.
 */
const SignDocumentModal: React.FC<SignDocumentModalProps> = ({
  doc,
  token,
  defaultSignerName,
  onClose,
  onSigned,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [renderedPages, setRenderedPages] = useState<number[]>([]);
  const [loadError, setLoadError] = useState(false);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [consentAgreed, setConsentAgreed] = useState(false);
  const [signerName, setSignerName] = useState(defaultSignerName);
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const signDocument = useSignOnboardingDocument(token);

  const isPdf = !!doc.pdf_url;
  const hasContent = isPdf || !!doc.content;
  // Only judge "read to the end" once there is a full document to scroll: while
  // pages are still rendering the container is short and would pass instantly.
  const contentReady = isPdf
    ? numPages !== null && renderedPages.length >= numPages
    : hasContent;

  const checkReachedEnd = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !contentReady) return;
    // Content shorter than the viewport is already fully visible; anything
    // taller has to be scrolled to the bottom.
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= SCROLL_END_SLACK_PX) {
      setReachedEnd(true);
    }
  }, [contentReady]);

  // The document grows as its pages render, so re-check the gate on every
  // layout change. The observer also fires once on observe, which covers a
  // document short enough that it never scrolls at all.
  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    const observer = new ResizeObserver(() => checkReachedEnd());
    observer.observe(content);
    return () => observer.disconnect();
  }, [checkReachedEnd]);

  const canSign = reachedEnd && consentAgreed && !!signerName.trim() && hasContent;

  const handleSignatureSave = async (signatureData: string) => {
    setError(null);
    try {
      const signed = await signDocument.mutateAsync({
        templateId: doc.template_id,
        data: {
          signature_data: signatureData,
          signer_name: signerName.trim(),
          consent_agreed: consentAgreed,
        },
      });
      onSigned(signed);
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <>
      <Modal open onClose={onClose}>
        <ModalDialog
          sx={{
            width: '95vw',
            maxWidth: 900,
            height: '92vh',
            p: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1,
              p: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box>
              <Typography level="title-lg">{doc.title}</Typography>
              <Typography level="body-sm" color="neutral">
                Read the whole document, then sign at the bottom.
              </Typography>
            </Box>
            <IconButton variant="plain" color="neutral" onClick={onClose}>
              <Close />
            </IconButton>
          </Box>

          <Box
            ref={scrollRef}
            onScroll={checkReachedEnd}
            sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: 'background.level2' }}
          >
            <Box
              ref={contentRef}
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
            >
              {isPdf && !loadError && (
                <Document
                  file={doc.pdf_url || undefined}
                  onLoadSuccess={({ numPages: pageCount }) => setNumPages(pageCount)}
                  onLoadError={() => setLoadError(true)}
                  onSourceError={() => setLoadError(true)}
                  loading={<CircularProgress sx={{ my: 4 }} />}
                >
                  {numPages &&
                    Array.from(new Array(numPages), (_, index) => (
                      <Box key={`page_${index + 1}`} sx={{ mb: 2 }}>
                        <Page
                          pageNumber={index + 1}
                          width={Math.min(820, window.innerWidth - 96)}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                          onRenderSuccess={() =>
                            setRenderedPages((prev) =>
                              prev.includes(index + 1) ? prev : [...prev, index + 1]
                            )
                          }
                          // A page that never renders would leave the gate shut
                          // forever, so fall back to reading the file directly.
                          onRenderError={() => setLoadError(true)}
                        />
                      </Box>
                    ))}
                </Document>
              )}

              {isPdf && loadError && (
                <Alert color="warning" variant="soft" sx={{ width: '100%' }}>
                  <Box>
                    <Typography level="body-sm" sx={{ mb: 1 }}>
                      We could not display this document here. Open it in a new tab to read it
                      before you sign.
                    </Typography>
                    <Button
                      component="a"
                      href={doc.pdf_url || undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="sm"
                      variant="outlined"
                      color="warning"
                      // The viewer failed, so there is no scroll position to trust.
                      // Opening the file is the strongest signal we can honestly
                      // collect — we do not quietly pass the gate on a render error.
                      onClick={() => setReachedEnd(true)}
                    >
                      Open document
                    </Button>
                  </Box>
                </Alert>
              )}

              {!isPdf && doc.content && (
                <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap', width: '100%' }}>
                  {doc.content}
                </Typography>
              )}

              {!hasContent && (
                <Alert color="warning" variant="soft" sx={{ width: '100%' }}>
                  This document has no content attached yet, so there is nothing to sign.
                  Please contact HR.
                </Alert>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              p: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            {error && <Alert color="danger">{error}</Alert>}

            {hasContent && !reachedEnd && (
              <Alert color="neutral" variant="soft">
                Scroll to the end of the document to enable signing.
              </Alert>
            )}

            <Checkbox
              checked={consentAgreed}
              disabled={!reachedEnd}
              onChange={(e) => setConsentAgreed(e.target.checked)}
              label={
                <Typography level="body-sm">
                  I agree to sign this document electronically. I understand my electronic
                  signature is legally binding and has the same effect as a signature
                  written by hand.
                </Typography>
              }
            />

            <FormControl>
              <FormLabel>Full legal name</FormLabel>
              <Input
                value={signerName}
                disabled={!reachedEnd}
                placeholder="Type your full name"
                onChange={(e) => setSignerName(e.target.value)}
              />
            </FormControl>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button variant="outlined" color="neutral" onClick={onClose}>
                Cancel
              </Button>
              <Button
                startDecorator={<DrawOutlined />}
                disabled={!canSign}
                loading={signDocument.isPending}
                onClick={() => setSignatureOpen(true)}
              >
                Sign Document
              </Button>
            </Box>
          </Box>
        </ModalDialog>
      </Modal>

      {/* The same signature pad the CRM uses for customer contracts. */}
      <SignatureModal
        open={signatureOpen}
        onClose={() => setSignatureOpen(false)}
        onSave={handleSignatureSave}
        fieldLabel={doc.title}
        signerName={signerName.trim() || defaultSignerName}
      />
    </>
  );
};

export const DocumentSigningStep: React.FC<ExtendedWizardStepProps> = ({
  sessionState,
  onDataChange,
  onNext,
  onBack,
  sessionData,
  token,
}) => {
  const [signedByTemplate, setSignedByTemplate] = useState<Record<string, OnboardingRequiredDocument>>({});
  const [activeDoc, setActiveDoc] = useState<OnboardingRequiredDocument | null>(null);

  // The server holds the signing record, so it is the source of truth here.
  // Documents signed in this sitting are merged on top so the row updates
  // before the session payload is refetched.
  const documents = (sessionData?.required_documents ?? []).map(
    (doc) => signedByTemplate[doc.template_id] ?? doc
  );

  const signedIds = documents.filter((doc) => doc.is_signed).map((doc) => doc.template_id);
  // Keyed on the ids themselves: a fresh array every render would otherwise
  // push step data on every render.
  const signedKey = signedIds.join(',');

  useEffect(() => {
    onDataChange({ signed_documents: signedIds });
  }, [signedKey]);

  const allDocumentsSigned = documents.every((doc) => !doc.requires_signature || doc.is_signed);
  const signedCount = signedIds.length;
  const totalRequired = documents.filter((doc) => doc.requires_signature).length;

  const handleSubmit = () => {
    if (allDocumentsSigned) {
      onNext();
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography level="h4" sx={{ mb: 1 }}>
        Your Documents
      </Typography>
      <Typography level="body-sm" color="neutral" sx={{ mb: 3 }}>
        Read and sign the documents your employer has shared.
      </Typography>

      <Alert color="primary" variant="soft" sx={{ mb: 3 }}>
        Read each document to the end, then sign it. We keep a signed copy with the date,
        your name and your signature, and you can download it here.
      </Alert>

      {documents.length === 0 ? (
        <Card variant="soft" sx={{ mb: 3 }}>
          <CardContent>
            <Typography level="body-sm" color="neutral" sx={{ textAlign: 'center' }}>
              Your employer has not attached any documents to this step.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <List sx={{ gap: 1 }}>
          {documents.map((doc) => {
            const isSigned = doc.is_signed;

            return (
              <ListItem
                key={doc.template_id}
                sx={{
                  borderRadius: 'md',
                  border: '1px solid',
                  borderColor: isSigned ? 'success.300' : 'divider',
                  bgcolor: isSigned ? 'success.softBg' : 'background.surface',
                }}
              >
                <ListItemDecorator>
                  {isSigned ? (
                    <CheckCircleOutlined color="var(--joy-palette-success-500)" />
                  ) : (
                    <DescriptionOutlined color="var(--joy-palette-primary-500)" />
                  )}
                </ListItemDecorator>
                <ListItemContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                    <Box>
                      <Typography level="title-sm">{doc.title}</Typography>
                      <Typography level="body-xs" color="neutral">
                        {doc.description}
                      </Typography>
                      {isSigned && doc.signed_at && (
                        <Typography level="body-xs" color="success">
                          Signed on {new Date(doc.signed_at).toLocaleDateString()}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {isSigned ? (
                        <>
                          <Chip color="success" variant="soft" startDecorator={<CheckCircleOutlined />}>
                            Signed
                          </Chip>
                          {doc.signed_document_url && (
                            <Button
                              component="a"
                              href={doc.signed_document_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              size="sm"
                              variant="outlined"
                              color="neutral"
                              startDecorator={<DownloadOutlined />}
                            >
                              Download
                            </Button>
                          )}
                        </>
                      ) : (
                        <Button
                          variant="soft"
                          color="primary"
                          startDecorator={<DrawOutlined />}
                          disabled={!token}
                          onClick={() => setActiveDoc(doc)}
                        >
                          Sign Document
                        </Button>
                      )}
                    </Box>
                  </Box>
                </ListItemContent>
              </ListItem>
            );
          })}
        </List>
      )}

      {/* Signing Progress */}
      <Card variant="soft" sx={{ mt: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography level="body-sm">
              Documents signed: {signedCount} of {totalRequired}
            </Typography>
            {allDocumentsSigned ? (
              <Chip color="success" variant="soft">
                All documents signed
              </Chip>
            ) : (
              <Chip color="warning" variant="soft" startDecorator={<PendingOutlined />}>
                Pending signatures
              </Chip>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button variant="outlined" color="neutral" onClick={onBack}>
          Back
        </Button>
        <Button onClick={handleSubmit} disabled={!allDocumentsSigned && totalRequired > 0}>
          Continue
        </Button>
      </Box>

      {activeDoc && token && (
        // Keyed so each document opens the reader with its own scroll gate,
        // consent and name rather than inheriting the previous one's.
        <SignDocumentModal
          key={activeDoc.template_id}
          doc={activeDoc}
          token={token}
          defaultSignerName={sessionState.team_member_name.trim()}
          onClose={() => setActiveDoc(null)}
          onSigned={(signed) =>
            setSignedByTemplate((prev) => ({ ...prev, [signed.template_id]: signed }))
          }
        />
      )}
    </Box>
  );
};

export default DocumentSigningStep;
